// this component is very similar to the `DeliverableInsightsTab` component.
// that's why some of the other Insights components and/or naming are reused here.
// essentially, "Upload" is just a rebranding of "Insights"
import * as React from 'react';
import { connect } from 'react-redux';

import DeliverableUploadPostUrl from './deliverable-upload-post-url/DeliverableUploadPostUrl';
import DeliverableAttachmentFeedbackModal from '../../deliverable-attachment-feedback-modal/DeliverableAttachmentFeedbackModal';
import DeliverableUpload from './deliverable-upload/DeliverableUpload';
import DeliverableTabFooter from '../deliverable-tab-footer/DeliverableTabFooter';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  isAnalyticsScreenshotRequired,
  isHighresImagesRequired } from 'utils/deliverables';
import { isInstagramPostUrl, isUrl, isValidTikTokVideoUrl, isYoutubeVideoUrl } from 'utils/validators';
import { getDeliverableTrackingActionType } from 'modules/deliverables/utils/tracking-helper';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { downloadDeliverableAttachments } from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { updateInsights } from 'modules/subcampaigns/reducers/offerDeliverables';
import { updateInsightsData } from 'modules/deliverables/reducers/deliverableInsights';
import { updateDeliverableInsights } from 'modules/campaigns/reducers/campaignDeliverables';

import {
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentType,
  DeliverablePost,
  DeliverableType,
  DeliverableUploadedEventAction,
  Subcampaign,
  SubcampaignInfluencerProfile } from 'types';

import './deliverable-upload-tab.scss';

applyTranslations(require('./deliverable-upload-tab.i18n.json'));

type OwnProps = {
  deliverable: Deliverable,
  isForBrand?: boolean,
  postAttachments: Array<DeliverableAttachment>,
  subcampaign: Subcampaign,
  isContentPage?: boolean,
  setDeliverableModified: (isModified: boolean) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
}

type DispatchProps = {
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null
  ) => void,
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message?: string,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode,
      confirmButtonLabel: string,
      onProceed?: () => void,
      onConfirm?: () => void,
      proceedButtonLabel?: string
    }
  ) => void,
  dispatchUpdateInsightsData: (
    offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    postUrl: string | null,
    isUrlChanged: boolean
  ) => Promise<Deliverable>,
  dispatchHideConfirmationModal: () => void,
  dipatchUpdateDeliverable: (
    offerId: number,
    deliverable: Deliverable,
    post?: DeliverablePost,
    isContentPage?: boolean) => void
}

type Props = OwnProps & DispatchProps;

type State = {
  postUrlError: string,
  isEditMode: boolean,
  postUrl?: string,
  attachments: Array<DeliverableAttachment>,
  post?: DeliverablePost,
  isModified: boolean
}

const VALIDATION_FN = {
  instagram_post: isInstagramPostUrl,
  tiktok_video: isValidTikTokVideoUrl,
  youtube_video: isYoutubeVideoUrl,
  other: isUrl
};

const msgKey = 'deliverable_upload_tab';

class DeliverableUploadTab extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { deliverable, isForBrand } = props;
    const { postAttachments, post } = deliverable;

    this.state = {
      postUrlError: '',
      isEditMode: !isForBrand,
      postUrl: post?.url,
      attachments: postAttachments || [],
      isModified: false,
      post: deliverable.post
    };
  }

  setEditMode = (isEditMode: boolean) => {
    this.setState(prevState => ({
      ...prevState,
      isEditMode
    }));
  };

  setAttachments = (newAttachments: Array<DeliverableAttachment>) => {
    this.setState(prevState => ({
      ...prevState,
      attachments: [...prevState.attachments, ...newAttachments]
    }));
  };

  handleOnTrackDeliverableUploadEvent(
    type: DeliverableType,
    content: 'url' | 'analytics' | 'high-res'
  ) {
    const { onTrackDeliverableUploadEvent } = this.props;
    const eventAction = getDeliverableTrackingActionType(type, content);

    if (eventAction === null) {
      return;
    }

    onTrackDeliverableUploadEvent(eventAction);
  }

  handleModification = (isDeliverableChanged: boolean, nextState: Partial<State>) => {
    const { setDeliverableModified } = this.props;

    this.setState(prevState => ({
      ...prevState,
      ...nextState,
      isModified: isDeliverableChanged
    }));
    setDeliverableModified(isDeliverableChanged);
  };

  handleUpdateAttachments = (
    tempAttachments: Array<DeliverableAttachment & { isInvalidImage?: boolean }>,
    attachmentType: DeliverableAttachmentType) => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal, deliverable } = this.props;
    const { deliverableType } = deliverable;

    const validAttachments = tempAttachments.filter(attachment => !attachment.isInvalidImage);
    const inValidAttachments = tempAttachments.filter(attachment => attachment.isInvalidImage);

    this.handleModification(true, { isModified: true });
    this.setAttachments(validAttachments);


    if (inValidAttachments.length) {
      dispatchShowConfirmationModal({
        innerContent: (
          <DeliverableAttachmentFeedbackModal
            deliverableType={deliverableType}
            attachmentType={attachmentType}
          />
        ),
        confirmButtonLabel: __t('deliverable_media_attachments.button_discard'),
        onConfirm: async () => {
          dispatchHideConfirmationModal();
        },
        proceedButtonLabel: __t('deliverable_media_attachments.button_upload'),
        onProceed: async () => {
          dispatchHideConfirmationModal();
          // attach invalid images anyway
          this.setAttachments(inValidAttachments);
        }
      });
    }
  };

  handleDeleteAttachments = (deliverableAttachments: Array<DeliverableAttachment>) => {
    const { attachments } = this.state;

    const updatedAttachments = attachments.filter(
      attachment => !deliverableAttachments.find(deliverableAttachment => deliverableAttachment.id === attachment.id));

    this.handleModification(true, { attachments: [...updatedAttachments] });
  };

  handleReplaceAttachment = (replacement: DeliverableAttachment, current: DeliverableAttachment) => {
    const { attachments } = this.state;

    const updatedAttachments = attachments.map(attachment => (current.id === attachment.id ? replacement : attachment));
    this.handleModification(true, { attachments: [...updatedAttachments] });
  };

  handlePostUrlChange = (url: string) => {
    this.handleModification(true, { postUrl: url, postUrlError: '', isModified: true });
  };

  handleDownloadAttachments = (approvedOnly?: boolean | null) => {
    const { subcampaign, deliverable, dispatchDownloadAttachments } = this.props;
    const { campaignId, creatorProfile } = subcampaign;

    dispatchDownloadAttachments(campaignId, creatorProfile, deliverable, undefined, approvedOnly);
  };

  saveInsightsData = () => {
    const { isForBrand, subcampaign, deliverable, isContentPage, dispatchUpdateInsightsData, dipatchUpdateDeliverable } = this.props;
    const { attachments, postUrl, post: statePost } = this.state;
    const { postAttachments: savedPostAttachments, post: savedPost, id } = deliverable;
    const { offer } = subcampaign;

    const addedAttachments = attachments.filter(attachment => !savedPostAttachments?.find(postAttachment => postAttachment.id === attachment.id));
    const deleteAttachments = (savedPostAttachments || [])
      .filter(postAttachment => !attachments.find(attachment => attachment.id === postAttachment.id))
      .map(postAttachment => postAttachment.id);

    const isUrlChanged = savedPost?.id && savedPost?.url !== postUrl;
    const updatedUrl = postUrl !== savedPost?.url ? postUrl : null;

    dispatchUpdateInsightsData(
      offer.id,
      id,
      addedAttachments, // @ts-ignore deletedAttachments will always have ids as number, as its extracted from BE data
      deleteAttachments,
      updatedUrl,
      isUrlChanged)
      .then((response) => {
        if (!response) {
          return;
        }

        if (updatedUrl) {
          this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'url');
        }

        if (addedAttachments.some(a => a.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS)) {
          this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'analytics');
        }

        if (addedAttachments.some(a => a.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES)) {
          this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'high-res');
        }

        this.handleModification(false, {
          isEditMode: !isForBrand,
          attachments: response.postAttachments || [],
          post: response.post || statePost
        });

        dipatchUpdateDeliverable(offer.id, response, response.post || statePost, isContentPage);
      });
  };

  shouldSave = () => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { isModified } = this.state;

    if (!isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.no_changes_modal.title`),
        message: __t(`${msgKey}.no_changes_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.no_changes_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onConfirm: () => dispatchHideConfirmationModal()
      });

      return false;
    }

    return true;
  };

  handleSave = () => {
    if (!this.shouldSave()) {
      return;
    }

    const { deliverable, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { postUrl, attachments, isModified } = this.state;
    const { deliverableType, postAttachments } = deliverable;

    const validation = VALIDATION_FN[deliverableType]?.(postUrl);
    const isValid = validation?.valid ?? true; // treat as valid if validation is undefined

    if (!(postUrl || attachments.length || postAttachments?.length) && !isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.add_content_modal.title`),
        message: __t(`${msgKey}.add_content_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.add_content_modal.continue_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onConfirm: () => dispatchHideConfirmationModal()
      });
    } else if (postUrl && !isValid) {
      this.setState(prevState => ({
        ...prevState,
        postUrlError: validation?.message
      }));
    } else {
      this.saveInsightsData();
    }
  };

  handleCancelChanges = () => {
    const { isModified } = this.state;
    const { deliverable, isForBrand, dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;
    const { postAttachments: savedPostAttachments, post: savedPost } = deliverable;

    if (isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.discard_changes_modal.title`),
        message: __t(`${msgKey}.discard_changes_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.discard_changes_modal.discard_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: () => {
          this.handleModification(false, {
            attachments: savedPostAttachments || [],
            postUrl: savedPost?.url,
            isEditMode: !isForBrand,
            postUrlError: '',
            post: savedPost
          });

          dispatchHideConfirmationModal();
        },
        innerContent: (
          <div className="deliverable-upload-tab__cofirmation-message">
            {__t(`${msgKey}.discard_changes_modal.confirmation_message`)}
          </div>
        )
      });
    } else {
      this.setState(prevState => ({
        ...prevState,
        isEditMode: !isForBrand
      }));
    }
  };

  render() {
    const { deliverable, isForBrand } = this.props;
    const { attachments, isEditMode, postUrl, post, postUrlError, isModified } = this.state;
    const { deliverableType, requiresApproval } = deliverable;

    const requiresHighResImages = isHighresImagesRequired(!!requiresApproval, deliverableType);
    const requiresAnalytics = isAnalyticsScreenshotRequired(deliverableType);

    return (
      <div className="deliverable-upload-tab">
        <div>
          <div className="deliverable-upload-tab__date-section">
            {`${__t(`${msgKey}.labels.upload_provision_date`)}`}
          </div>
          <div className="deliverable-upload-tab__subheadline">
            {__t(`${msgKey}.headline.${isForBrand ? 'brand' : 'influencer'}.${deliverableType}.upload_subheadline${requiresApproval ? '' : '_without_approval'}`)}
          </div>
        </div>
        {
          deliverableType !== DELIVERABLE_TYPE_INSTAGRAM_STORY && (
            <DeliverableUploadPostUrl
              isEditMode={isEditMode}
              deliverable={deliverable}
              deliverablePost={post}
              postUrl={postUrl}
              onSetPostUrl={this.handlePostUrlChange}
              onSetEditMode={() => this.setEditMode(!isEditMode)}
              postUrlError={postUrlError}
              onDeleteUrl={() =>
                this.handleModification(true, { post: undefined, postUrl: '', postUrlError: '' })
              }
              onEditUrl={() =>
                this.handleModification(true, { post: undefined, postUrlError: '' })
              }
              isForBrand={isForBrand}
            />
          )
        }
        {
          requiresAnalytics && (<DeliverableUpload
            isForBrand={isForBrand}
            isEditMode={isEditMode}
            deliverable={deliverable}
            onEditMode={this.setEditMode}
            attachments={attachments.filter(attachment => attachment.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS)}
            onUpdate={(tempAttachments) =>
              this.handleUpdateAttachments(tempAttachments, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS)}
            onDeleteAttachments={this.handleDeleteAttachments}
            onReplaceAttachment={this.handleReplaceAttachment}
            mode={DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS}
          />)
        }
        {
          requiresHighResImages && (
            <DeliverableUpload
              isForBrand={isForBrand}
              isEditMode={isEditMode}
              deliverable={deliverable}
              onEditMode={this.setEditMode}
              attachments={attachments.filter(attachment => attachment.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES)}
              onUpdate={(tempAttachments) =>
                this.handleUpdateAttachments(tempAttachments, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES)}
              onDeleteAttachments={this.handleDeleteAttachments}
              onReplaceAttachment={this.handleReplaceAttachment}
              mode={DELIVERABLE_ATTACHMENT_TYPE_HIGHRES}
            />
          )
        }
        <div className="deliverable-upload-tab__section deliverable-upload-tab__actions">
          <DeliverableTabFooter
            onDownloadAttachments={this.handleDownloadAttachments}
            deliverable={deliverable}
            isEditMode={isEditMode}
            onEditModeToggle={this.setEditMode}
            attachments={attachments
              .filter(attachment => [DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES].includes(attachment.meta?.type))}
            isForBrand={isForBrand}
            isModified={isModified}
            onSave={this.handleSave}
            onDeleteAttachments={this.handleDeleteAttachments}
            isInsights
            onCancelChanges={this.handleCancelChanges}
          />
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchUpdateInsightsData: (offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    postUrl: string | null,
    isUrlChanged: boolean) =>
    dispatch(updateInsightsData(offerId, deliverableId, addAttachments, deleteAttachments, postUrl, isUrlChanged)),
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null) =>
    dispatch(downloadDeliverableAttachments(campaignId, creatorProfile, deliverable, attachmentType, approvedOnly)),
  dipatchUpdateDeliverable: (
    offerId: number,
    deliverable: Deliverable,
    post?: DeliverablePost,
    isContentPage?: boolean) => (
    dispatch(isContentPage ?
      updateDeliverableInsights(deliverable, post) :
      updateInsights(offerId, deliverable, post))
  )
});

export default connect(null, mapDispatchToProps)(DeliverableUploadTab);
